import { useState, useEffect, useRef, useCallback } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { L } from '../ui/LavDropdown'
import { StIc, stGhostBtnSx } from './stBits'
import ImageCropDialog from '../ImageCropDialog'
import {
	fetchMerchantLogo,
	uploadMerchantLogo,
	deleteMerchantPhoto,
	getMerchantPhotoUrl,
} from '../../utils/merchantPhotoUtils'

const ACCEPT = 'image/jpeg,image/jpg,image/png'

function initials(name) {
	const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
	if (!parts.length) return '?'
	return parts.slice(0, 2).map((w) => w[0].toUpperCase()).join('')
}

/**
 * Business logo — circular upload slot per the Settings.html design
 * (`st-logo`). Click or drop an image on the circle; initials stand in until
 * one is uploaded. Saves straight to `merchant_photos` (is_avatar = true), so
 * it does not take part in the page's Save / Discard bar.
 *
 * `stacked` = mobile section screen (slightly smaller circle, taller buttons).
 */
export default function BusinessLogoUpload({ merchant, stacked = false, onChange }) {
	const merchantId = merchant?.id
	const inputRef = useRef(null)
	const [logo, setLogo] = useState(null)
	const [loading, setLoading] = useState(Boolean(merchantId))
	const [uploading, setUploading] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [drag, setDrag] = useState(false)
	const [cropFile, setCropFile] = useState(null)
	const [error, setError] = useState('')

	useEffect(() => {
		let cancelled = false
		if (!merchantId) { setLoading(false); return undefined }
		setLoading(true)
		fetchMerchantLogo(merchantId)
			.then((photo) => { if (!cancelled) setLogo(photo) })
			.catch((e) => console.error('Error loading business logo:', e))
			.finally(() => { if (!cancelled) setLoading(false) })
		return () => { cancelled = true }
	}, [merchantId])

	const pick = () => inputRef.current?.click()

	const takeFile = useCallback((file) => {
		if (!file) return
		if (!/^image\/(jpeg|jpg|png)$/i.test(file.type)) {
			setError('Please choose a JPG or PNG image.')
			return
		}
		setError('')
		setCropFile(file)
	}, [])

	const handleCropComplete = async (blob) => {
		if (!merchantId) return
		setUploading(true)
		setError('')
		try {
			const result = await uploadMerchantLogo(merchantId, blob, logo?.id)
			if (result?.photo) {
				setLogo(result.photo)
				onChange && onChange(result.photo)
			} else {
				setError('Failed to upload logo. Please try again.')
			}
		} catch (e) {
			console.error('Error uploading logo:', e)
			setError('Failed to upload logo. Please try again.')
		} finally {
			setUploading(false)
		}
	}

	const handleRemove = async () => {
		if (!logo || deleting) return
		setDeleting(true)
		setError('')
		try {
			const result = await deleteMerchantPhoto(logo.id)
			if (result === null) return // user cancelled the confirm
			if (result) {
				setLogo(null)
				onChange && onChange(null)
			} else {
				setError('Failed to remove logo. Please try again.')
			}
		} catch (e) {
			console.error('Error removing logo:', e)
			setError('Failed to remove logo. Please try again.')
		} finally {
			setDeleting(false)
		}
	}

	const has = Boolean(logo)
	const url = has ? getMerchantPhotoUrl(logo) : null
	const busy = uploading || deleting
	const circle = stacked ? 80 : 88
	const initSize = stacked ? 24 : 27

	const description = drag
		? 'Drop to use this image.'
		: has
			? 'Shown as a circle next to your business name.'
			: 'Square PNG or JPG, at least 400 × 400 px. Drop a file on the circle or browse.'

	return (
		<>
			<Box
				onDragOver={(e) => { e.preventDefault(); if (!drag) setDrag(true) }}
				onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false) }}
				onDrop={(e) => { e.preventDefault(); setDrag(false); takeFile(e.dataTransfer?.files?.[0]) }}
				sx={{ display: 'flex', alignItems: 'center', gap: stacked ? '15px' : '18px', minWidth: 0, fontFamily: L.font }}
			>
				{/* circular slot */}
				<Box
					component="button"
					type="button"
					onClick={pick}
					disabled={busy || loading}
					aria-label={has ? 'Change business logo' : 'Upload business logo'}
					title={has ? 'Change logo' : 'Upload logo'}
					sx={{
						position: 'relative', width: circle, height: circle, flex: 'none', p: 0, borderRadius: '50%',
						cursor: busy || loading ? 'default' : 'pointer',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						transition: 'border-color .15s, background .15s, box-shadow .15s, transform .15s',
						...(has
							? { border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF' }
							: { border: '1.5px dashed #C9C2E4', bgcolor: L.surface }),
						...(drag ? {
							border: `1.5px dashed ${L.primary}`, bgcolor: L.primarySoft,
							boxShadow: `0 0 0 4px ${L.primarySoft}`, transform: 'scale(1.03)',
						} : {}),
						'&:hover:not(:disabled)': has
							? { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` }
							: { borderColor: L.primary, bgcolor: L.primarySoft },
						'&:hover:not(:disabled) .st-logoinit': { color: L.primaryDeep },
						'&:hover:not(:disabled) .st-logobadge': { bgcolor: L.primaryDeep },
						'&:focus-visible': { outline: 'none', borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
					}}
				>
					{loading || busy ? (
						<CircularProgress size={22} sx={{ color: L.primary }} />
					) : has ? (
						<Box
							component="img"
							src={url}
							alt=""
							sx={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
						/>
					) : (
						<Box
							component="span"
							className="st-logoinit"
							sx={{
								fontSize: initSize, fontWeight: 800, letterSpacing: '0.02em',
								color: drag ? L.primaryDeep : '#A79FBE', transition: 'color .15s', fontFamily: L.font,
							}}
						>
							{initials(merchant?.name)}
						</Box>
					)}
					<Box
						component="span"
						className="st-logobadge"
						sx={{
							position: 'absolute', right: -3, bottom: -3, width: stacked ? 27 : 30, height: stacked ? 27 : 30,
							borderRadius: '50%', bgcolor: L.primary, color: '#FFFFFF',
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							border: '2.5px solid #FFFFFF', boxShadow: '0 2px 8px rgba(124,107,217,0.32)', transition: 'background .15s',
						}}
					>
						<StIc name={has ? 'edit' : 'camera'} size={14} sw={1.7} />
					</Box>
				</Box>

				{/* text + actions */}
				<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
					<Box component="strong" sx={{ fontSize: '14px', fontWeight: 750, letterSpacing: '-0.01em', color: L.text }}>
						Business logo
					</Box>
					<Box
						component="span"
						sx={{
							fontSize: '12.5px', fontWeight: 600, lineHeight: 1.5, maxWidth: '52ch', textWrap: 'pretty',
							color: drag ? L.primaryDeep : L.muted,
						}}
					>
						{description}
					</Box>
					{error && (
						<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, lineHeight: 1.5, color: '#BE4B45' }}>
							{error}
						</Box>
					)}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', mt: '8px' }}>
						<Box
							component="button"
							type="button"
							onClick={pick}
							disabled={busy || loading}
							sx={{
								...stGhostBtnSx,
								...(stacked ? { height: 40, px: '14px', fontSize: '13.5px', borderRadius: '10px' } : {}),
							}}
						>
							<StIc name="upload" size={14} />
							{has ? 'Replace logo' : 'Upload logo'}
						</Box>
						{has && (
							<Box
								component="button"
								type="button"
								onClick={handleRemove}
								disabled={busy}
								sx={{
									display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
									height: stacked ? 40 : 34, border: 'none', bgcolor: 'transparent', color: '#BE4B45',
									fontSize: '12.5px', fontWeight: 650, px: '10px', borderRadius: '7px', cursor: 'pointer',
									fontFamily: L.font,
									'&:hover:not(:disabled)': { bgcolor: '#FBECEB' },
									'&:disabled': { opacity: 0.55, cursor: 'default' },
								}}
							>
								Remove
							</Box>
						)}
					</Box>
				</Box>

				<input
					ref={inputRef}
					type="file"
					accept={ACCEPT}
					style={{ display: 'none' }}
					onChange={(e) => { takeFile(e.target.files?.[0]); e.target.value = '' }}
				/>
			</Box>

			<ImageCropDialog
				open={Boolean(cropFile)}
				onClose={() => setCropFile(null)}
				imageFile={cropFile}
				onCropComplete={handleCropComplete}
				aspectRatio={1}
				outputWidth={500}
				outputHeight={500}
				circularCrop={false}
				title="Crop Logo"
			/>
		</>
	)
}
